import React from 'react';
import { FiUser, FiUserPlus, FiClock } from 'react-icons/fi';

export default function TaskAssignmentTable({ assignments = [], visible = false }) {
  if (!visible) return null;

  console.log("TaskAssignmentTable - Raw assignments:", assignments);
  
  return (
    <div className="overflow-auto max-h-96 shadow-md rounded-md">
      <table className="min-w-full bg-white">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status Change</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {assignments.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-4 text-center text-sm text-gray-500">
                No assignment history available
              </td>
            </tr>
          ) : (
            assignments.map((assignment, index) => {
              // Extract and format data with multiple fallbacks
              
              // FORMAT ASSIGNED BY INFORMATION
              // First try the assignedBy object from enhanced backend
              let assignedByName = "Unknown User";
              let assignedByRole = "Staff Member";
              
              if (assignment.assignedBy) {
                assignedByName = assignment.assignedBy.name || "Unknown User";
                assignedByRole = assignment.assignedBy.role || "Staff Member";
              } else if (assignment.assignedById) {
                // Use assignedById instead of assignedByAdmin/assignedByEmployee
                assignedByName = "User " + assignment.assignedById;
                // Set role based on userType
                assignedByRole = assignment.assignedByUserType === "ADMIN" ? 
                  "Admin" : "Staff Member";
              }
              
              // FORMAT ASSIGNED TO INFORMATION
              // First try the assignedTo object from enhanced backend
              let assignedToName = "Unknown User";
              let assignedToRole = "Staff Member";
              
              if (assignment.assignedTo) {
                assignedToName = assignment.assignedTo.name || "Unknown User";
                assignedToRole = assignment.assignedTo.role || "Staff Member";
              } else if (assignment.admin) {
                // Legacy format fallback
                assignedToName = assignment.admin.fullname || "Admin User";
                assignedToRole = assignment.admin.designation?.name || "Admin"; // Use actual designation
              } else if (assignment.employee) {
                // Legacy format fallback
                assignedToName = assignment.employee.fullname || "Employee";
                assignedToRole = assignment.employee.designation?.name || "Staff Member";
              }
              
              // Format date string in dd-mm-yyyy format
              const formatDate = (dateValue) => {
                if (!dateValue) return "Unknown Date";
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return "Invalid Date";
                
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              };
              
              const formatTime = (dateValue) => {
                if (!dateValue) return "Unknown Time";
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return "Invalid Time";
                return date.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                });
              };
              
              const dateValue = assignment.assignedDate || assignment.assignedDateAndTime;
              const date = assignment.date || formatDate(dateValue);
              const time = assignment.time || formatTime(dateValue);
              
              // Calculate status change by comparing with previous entry
              const getStatusChange = (currentIndex) => {
                const currentStatus = assignment.status;
                const previousAssignment = assignments[currentIndex + 1]; // Next in array is previous in time
                const previousStatus = previousAssignment?.status;
                
                if (!previousStatus) {
                  // This is the first assignment (project creation)
                  return `Initial → ${formatStatusForDisplay(currentStatus)}`;
                }
                
                if (currentStatus === previousStatus) {
                  // Status didn't change, just reassignment
                  return `${formatStatusForDisplay(currentStatus)} (Reassigned)`;
                }
                
                // Status changed
                return `${formatStatusForDisplay(previousStatus)} → ${formatStatusForDisplay(currentStatus)}`;
              };
              
              // Helper function to format status for display
              const formatStatusForDisplay = (status) => {
                if (!status) return 'Unknown';
                
                // Convert to title case and handle special cases
                const formatted = status.replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
                
                // Handle special cases
                switch (status.toUpperCase()) {
                  case 'IN_PROGRESS':
                    return 'In Progress';
                  case 'PAYMENT_RECEIVED':
                    return 'Payment Received';
                  case 'NON_ACTIVE':
                    return 'Non Active';
                  default:
                    return formatted;
                }
              };
              
              const statusChange = getStatusChange(index);
              
              return (
                <tr key={assignment.id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <FiUserPlus className="text-gray-500 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {assignedByName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {assignedByRole}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <FiUser className="text-gray-500 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {assignedToName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {assignedToRole}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mr-2 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {statusChange}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <FiClock className="text-gray-500 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{date}</div>
                        <div className="text-xs text-gray-500">{time}</div>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
